import { ACH_LADDERS, ACH_SPHERES } from "../model/types";
import type {
    AchCollectionRow,
    AchForm,
    AchItem,
    AchLadder,
    AchLadderState,
    AchLevel,
    AchSphere,
    AchievementsView,
} from "../model/types";

/**
 * Разбор достижений из ответов сервера (PLAN §3.3, §3.6) — общий для своего
 * /api/achievements/me (api/achievementsApi.ts) и для ЧУЖИХ видов (§5 F4):
 *  - профиль человека в общине: воркер GET /people/{id} → person.achievements
 *    (проекция public/<pid>.json как есть, W1);
 *  - строки списков людей: воркер /people/search, /friends, /friends/of → pins
 *    ({top, levels?, form?} из public_index.json, W1);
 *  - профиль участника лиги: GET /api/league/users/{pid}/profile → achievements (S6).
 *
 * Модуль лёгкий — ни каталога, ни картинок: его тянут клиенты людей и лиги.
 * Ключи, которых нет в каталоге, отсеивает уже модель вида (lib/viewModel.ts).
 *
 * Чужой вид — только взятое (R2): ни value / next / points / дат, ни ступени 0
 * (строки «только прогресс» бывают лишь в своём виде). Что бы ни прислали —
 * self здесь всегда false.
 */

const KEY_RE = /^[a-z0-9_]{1,64}$/;
/** id картинки каталога: l-stat-water-f-3 (§3.2). */
const IMG_RE = /^[a-z0-9_-]{1,80}$/;
/** Упражнение картинки лестницы тренировок: water, pose, tree, bath, hot (незнакомое каталогу — первое). */
const VARIANT_RE = /^[a-z]{1,16}$/;

export const isObj = (v: unknown): v is Record<string, unknown> => !!v && typeof v === "object" && !Array.isArray(v);
export const num = (v: unknown): number | undefined => (typeof v === "number" && Number.isFinite(v) ? v : undefined);
export const int = (v: unknown, lo: number, hi: number): number | undefined =>
    typeof v === "number" && Number.isInteger(v) && v >= lo && v <= hi ? v : undefined;
export const str = (v: unknown, max: number): string | undefined =>
    typeof v === "string" && v.trim() ? v.trim().slice(0, max) : undefined;
export const achKey = (v: unknown): string | undefined => (typeof v === "string" && KEY_RE.test(v) ? v : undefined);
export const imgId = (v: unknown): string | undefined => (typeof v === "string" && IMG_RE.test(v) ? v : undefined);
export const variantKey = (v: unknown): string | undefined =>
    typeof v === "string" && VARIANT_RE.test(v) ? v : undefined;
/** Доля участников, % (0–100): «есть у N % участников» и ключ 2 порядка взятого (lib/significance). */
export const pct = (v: unknown): number | undefined => {
    const n = num(v);
    return n !== undefined && n >= 0 && n <= 100 ? n : undefined;
};

/**
 * Лестницы тренировок (ADDENDUM §6–§7): {workouts | dyn | stat | therm | fast: строка}. row
 * разбирает одну строку; незнакомые лестницы и битые строки отбрасываются. Пусто — undefined.
 */
export const normLadders = (
    v: unknown,
    row: (x: unknown) => AchLadderState | null,
): Partial<Record<AchLadder, AchLadderState>> | undefined => {
    if (!isObj(v)) return undefined;
    const out: Partial<Record<AchLadder, AchLadderState>> = {};
    ACH_LADDERS.forEach((k) => {
        const r = row(v[k]);
        if (r) out[k] = r;
    });
    return Object.keys(out).length ? out : undefined;
};

/** Без пустых полей: {key} вместо {key, tier: undefined, …}. */
export const compact = <T extends object>(o: T): T => {
    const out = {} as Record<string, unknown>;
    Object.entries(o).forEach(([k, v]) => {
        if (v !== undefined) out[k] = v;
    });
    return out as T;
};

export const list = <T>(v: unknown, fn: (x: unknown) => T | null): T[] =>
    Array.isArray(v) ? v.map(fn).filter((x): x is T => x !== null) : [];

export const normForm = (v: unknown): AchForm | null => (v === "f" || v === "m" ? v : null);

/** Строка коллекции (памятное, вручённое): год, событие, город, заметка администратора; share — доля за этот год. */
export const normRow = (v: unknown): AchCollectionRow | null => {
    if (!isObj(v)) return null;
    const k = achKey(v.key);
    if (!k) return null;
    return compact({
        key: k,
        year: int(v.year, 1900, 3000),
        event: str(v.event, 160),
        city: str(v.city, 100),
        note: str(v.note, 200),
        share: pct(v.share),
    });
};

/**
 * Взятое: ключ со ступенью (≥ 1) или один ключ — разовое, памятное или закрытый
 * результат (только факт, R8). Ступень 0 или не число — строки нет: «без
 * ступени» значит «взято», и прогресс за взятое не выдать.
 */
const earnedKeyTier = (v: unknown): { key: string; tier?: number } | null => {
    if (!isObj(v)) return null;
    const k = achKey(v.key);
    if (!k) return null;
    if (v.tier === undefined || v.tier === null) return { key: k };
    const tier = int(v.tier, 1, 99);
    return tier === undefined ? null : { key: k, tier };
};

/** Три лучших (top): ключ и, у ступенчатых, ступень. */
export const normTop = (v: unknown): { key: string; tier?: number }[] => list(v, earnedKeyTier);

// ── Чужой вид ──────────────────────────────────────────────────────────────

/** Уровень другого: число (проекция: {"social": 3}) или {level}; только взятый (≥ 1), без баллов и дат. */
const otherLevel = (v: unknown): AchLevel | null => {
    const n = isObj(v) ? int(v.level, 1, 99) : int(v, 1, 99);
    return n === undefined ? null : { level: n };
};

/** Лестница другого: только взятый уровень (≥ 1) и id картинки — без значений, порогов и упражнения (R2). */
const otherLadder = (v: unknown): AchLadderState | null => {
    if (!isObj(v)) return null;
    const level = int(v.level, 1, 99);
    return level === undefined ? null : compact({ level, img: imgId(v.img) });
};

const otherLevels = (v: unknown): Partial<Record<AchSphere, AchLevel>> | undefined => {
    if (!isObj(v)) return undefined;
    const out: Partial<Record<AchSphere, AchLevel>> = {};
    ACH_SPHERES.forEach((s) => {
        const lv = otherLevel(v[s]);
        if (lv) out[s] = lv;
    });
    return Object.keys(out).length ? out : undefined;
};

/** Достижения другого человека (проекция §3.3). Не объект — null (поля нет). */
export const normOther = (body: unknown): AchievementsView | null => {
    if (!isObj(body)) return null;
    const view: AchievementsView = {
        self: false,
        form: normForm(body.form),
        items: list<AchItem>(body.items, earnedKeyTier),
        collection: list(body.collection, normRow),
    };
    // Уровни — только если сервер их прислал (смотрящий видит баллы v8.2, §3.5);
    // с ними — лестницы тренировок: уровень и картинка по полу и упражнению человека.
    const levels = otherLevels(body.levels);
    if (levels) view.levels = levels;
    const ladders = normLadders(body.ladders, otherLadder);
    if (ladders) view.ladders = ladders;
    const top = normTop(body.top);
    if (top.length) view.top = top;
    return view;
};

/**
 * Значки строки списка (W1: {top: [{key, tier?}] ≤ 3, levels?: {sphere: n}, form?: "f"}) —
 * как вид другого человека без списков: только уровни и лучшие. Нечего показать — null.
 * Общий уровень тренировок — levels.workouts; его картинка — по форме (у него нет упражнений).
 */
export const normPins = (body: unknown): AchievementsView | null => {
    if (!isObj(body)) return null;
    const top = normTop(body.top).slice(0, 3);
    const levels = otherLevels(body.levels);
    if (!top.length && !levels) return null;
    const view: AchievementsView = { self: false, form: normForm(body.form), items: [], collection: [] };
    if (levels) view.levels = levels;
    if (top.length) view.top = top;
    return view;
};

/** Есть ли что показать: взятый уровень, достижение или памятная строка. */
export const hasAchievements = (view: AchievementsView | null | undefined): view is AchievementsView =>
    !!view &&
    (view.items.length > 0 ||
        view.collection.length > 0 ||
        Object.keys(view.levels || {}).length > 0 ||
        Object.keys(view.ladders || {}).length > 0);
